// Full-bleed illustrated background for Login/Register/ForgotPassword.
// Fixed to the viewport so it fills the whole screen behind the centered
// form card, regardless of page scroll height. Built as original SVG/CSS
// (not a stock photo) — themed around the Cry parasporal crystal + DNA helix,
// using bg-brand/text-brand so it inherits whatever your brand color actually is.
const AuthBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden bg-slate-950" aria-hidden="true">
    <style>{`
      @keyframes auth-bg-drift {
        0%   { transform: translate(0, 0) rotate(0deg); }
        50%  { transform: translate(-16px, 14px) rotate(2deg); }
        100% { transform: translate(0, 0) rotate(0deg); }
      }
      @keyframes auth-bg-drift-slow {
        0%   { transform: translate(0, 0); }
        50%  { transform: translate(12px, -18px); }
        100% { transform: translate(0, 0); }
      }
      @keyframes auth-bg-twinkle {
        0%, 100% { opacity: 0.25; }
        50% { opacity: 0.9; }
      }
      .auth-bg-crystals { animation: auth-bg-drift 26s ease-in-out infinite; }
      .auth-bg-helix { animation: auth-bg-drift-slow 20s ease-in-out infinite; }
      .auth-bg-dot { animation: auth-bg-twinkle 4s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        .auth-bg-crystals, .auth-bg-helix, .auth-bg-dot { animation: none; }
      }
    `}</style>

    {/* mesh-gradient style glow blobs over the dark base */}
    <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-brand opacity-30 blur-[120px]" />
    <div className="absolute top-1/3 -left-32 w-[520px] h-[520px] rounded-full bg-brand opacity-20 blur-[120px]" />
    <div className="absolute bottom-0 right-1/4 w-[480px] h-[480px] rounded-full bg-brand opacity-25 blur-[110px]" />

    <svg
      className="absolute inset-0 w-full h-full text-brand"
      viewBox="0 0 1200 1000"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
    >
      {/* large faceted Cry crystal cluster, top-right */}
      <g className="auth-bg-crystals">
        <polygon points="900,60 970,100 970,180 900,220 830,180 830,100" fill="currentColor" opacity="0.18" />
        <polygon points="900,60 970,100 970,180 900,220 830,180 830,100" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
        <polygon points="1010,160 1070,194 1070,262 1010,296 950,262 950,194" fill="currentColor" opacity="0.14" />
        <polygon points="1010,160 1070,194 1070,262 1010,296 950,262 950,194" stroke="currentColor" strokeWidth="1.2" opacity="0.45" />
        <polygon points="880,260 935,291 935,353 880,384 825,353 825,291" fill="currentColor" opacity="0.12" />
        <polygon points="880,260 935,291 935,353 880,384 825,353 825,291" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <line x1="970" y1="180" x2="1010" y2="160" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <line x1="950" y1="262" x2="880" y2="260" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      </g>

      {/* smaller crystal cluster, bottom-left, for balance */}
      <g className="auth-bg-crystals" style={{ animationDelay: '4s' }}>
        <polygon points="120,760 180,793 180,859 120,892 60,859 60,793" fill="currentColor" opacity="0.14" />
        <polygon points="120,760 180,793 180,859 120,892 60,859 60,793" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
        <polygon points="220,840 270,867 270,921 220,948 170,921 170,867" fill="currentColor" opacity="0.10" />
        <polygon points="220,840 270,867 270,921 220,948 170,921 170,867" stroke="currentColor" strokeWidth="1" opacity="0.35" />
        <line x1="180" y1="859" x2="220" y2="840" stroke="currentColor" strokeWidth="1" opacity="0.35" />
      </g>

      {/* bright DNA double helix sweeping diagonally */}
      <g className="auth-bg-helix">
        <path
          d="M -80 940 C 100 860, 180 720, 340 660 C 500 600, 580 460, 740 400 C 860 350, 920 260, 1080 190"
          stroke="currentColor" strokeWidth="2" opacity="0.35"
        />
        <path
          d="M -80 1010 C 110 920, 190 770, 350 710 C 510 650, 590 500, 750 440 C 870 390, 930 290, 1080 230"
          stroke="currentColor" strokeWidth="2" opacity="0.3"
        />
        {Array.from({ length: 16 }).map((_, i) => {
          const t = i / 15;
          const x1 = -80 + t * 1160;
          const y1 = 940 - t * 750;
          const x2 = -80 + t * 1160;
          const y2 = 1010 - t * 780;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.5" opacity="0.22" />;
        })}
      </g>

      {/* scattered glowing base-pair dots for texture */}
      {[
        [200, 150], [340, 90], [520, 220], [150, 480], [1080, 540],
        [950, 700], [700, 850], [420, 920], [1120, 380], [60, 620],
      ].map(([cx, cy], i) => (
        <circle
          key={i}
          className="auth-bg-dot"
          cx={cx}
          cy={cy}
          r={i % 3 === 0 ? 3 : 2}
          fill="currentColor"
          opacity="0.5"
          style={{ animationDelay: `${i * 0.4}s` }}
        />
      ))}
    </svg>

    {/* subtle vignette so edges stay dark and the centered card pops */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-950/60" />
  </div>
);

export default AuthBackground;